import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class GeocodingService {
  private mapboxUrl = "https://api.mapbox.com/geocoding/v5/mapbox.places/";
  private accessToken = environment.mapboxPublickAccessToken;

  constructor(private http: HttpClient) {}

  geocode(query: string): Observable<GeoCodingResults> {
    // California bounding box (approximate)
    const bbox = "-124.409591,32.534156,-114.131211,42.009518";

    const params = new HttpParams()
      .set("access_token", this.accessToken)
      .set("bbox", bbox) // Restrict search to California
      .set("limit", "5"); // Limit the number of results

    return this.http.get<GeoCodingResults>(
      `${this.mapboxUrl}${encodeURIComponent(query)}.json`,
      {
        params,
      },
    );
  }

  /**
   * Check if a string is a valid lat/long coordinate format
   * Supports formats: "lat, long" or "lat,long" (e.g., "35.4994, -119.165665")
   */
  isLatLongFormat(query: string): boolean {
    const latLongRe = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
    return latLongRe.test(query.trim());
  }

  /**
   * Parse lat/long string and return coordinates
   * Input format: "lat, long" (e.g., "35.4994, -119.165665")
   * Returns: { lat, lng } or null if invalid
   */
  parseLatLong(query: string): { lat: number; lng: number } | null {
    if (!this.isLatLongFormat(query)) return null;
    const parts = query.split(",").map((p) => parseFloat(p.trim()));
    if (parts.length !== 2 || parts.some(isNaN)) return null;
    return { lat: parts[0], lng: parts[1] };
  }

  // Reverse geocode from coordinates - expects "long,lat" format for Mapbox API
  geocodeReverse(lng: number, lat: number): Observable<GeoCodingResults> {
    const params = new HttpParams()
      .set("access_token", this.accessToken)
      .set("limit", "1");

    // Mapbox expects longitude,latitude format
    const query = `${lng},${lat}`;
    return this.http.get<GeoCodingResults>(
      `${this.mapboxUrl}${encodeURIComponent(query)}.json`,
      {
        params,
      },
    );
  }
}

interface GeoCodingFeatureProperties {
  accuracy?: string; // e.g., "point"
  wikidata?: string; // e.g., "Q62"
  short_code?: string; // e.g., "US-CA"
  [x: string]: any; // Allow for additional properties
}

export interface GeoCodingFeatures {
  id: string; // e.g., "place.123456"
  type: string; // e.g., "Feature"
  place_type: string[]; // e.g., ["place"]
  relevance: number; // e.g., 1
  address?: string; // e.g., "94103"
  properties: GeoCodingFeatureProperties;
  text: string; // e.g., "San Francisco"
  place_name: string; // e.g., "San Francisco, California, United States"
  matching_text?: string; // e.g., "San Francisco"
  matching_place_name?: string; // e.g., "San Francisco, California, United States"
  language?: string; // e.g., "en"
  bbox: number[]; // e.g., [-122.5149, 37.7081, -122.357, 37.8324]
  center: number[]; // e.g., [-122.4194, 37.7749]
  geometry: {
    type: string; // e.g., "Point"
    coordinates: number[]; // e.g., [-122.4194, 37.7749]
    interpolated?: boolean; // e.g., false
    omitted?: boolean; // e.g., false
  };
  context: Array<
    Exclude<GeoCodingFeatures, "context"> & GeoCodingFeatureProperties
  >;
  routable_points?: {
    points: Array<{ coordinates: [number, number] }> | null;
  };
}
export interface GeoCodingResults {
  type: string; // e.g., "FeatureCollection"
  query: string[];
  features: GeoCodingFeatures[];
  attribution: string; // e.g., "© Mapbox © OpenStreetMap"
}
